import React from "react";
import {
  PaginationWrapper,
  PaginationList,
  PaginationItem,
  PaginationArrow,
} from "../../styles/dashboard";
import Back from "../../assets/svgs/Pagination/back.svg";
import Next from "../../assets/svgs/Pagination/next.svg";

// Constant for dots representation
export const DOTS = "...";

// Helper function to generate range of numbers
const range = (start: number, end: number) => {
  return Array.from({ length: end - start + 1 }, (_, idx) => idx + start);
};

// Custom hook for pagination logic
export const usePagination = ({
  totalPageCount,
  siblingCount = 1,
  currentPage,
}: {
  totalPageCount: number;
  siblingCount?: number;
  currentPage: number;
}) => {
  return React.useMemo(() => {
    // Calculate total page numbers to display
    const totalPageNumbers = siblingCount + 5;

    // If total pages is less than page numbers we want to show
    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(
      currentPage + siblingCount,
      totalPageCount
    );

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    // No left dots, but right dots
    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = 3 + 2 * siblingCount;
      let leftRange = range(1, leftItemCount);
      return [...leftRange, DOTS, totalPageCount];
    }

    // No right dots, but left dots
    if (shouldShowLeftDots && !shouldShowRightDots) {
      let rightItemCount = 3 + 2 * siblingCount;
      let rightRange = range(
        totalPageCount - rightItemCount + 1,
        totalPageCount
      );
      return [firstPageIndex, DOTS, ...rightRange];
    }

    // Both left and right dots
    if (shouldShowLeftDots && shouldShowRightDots) {
      let middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }
  }, [totalPageCount, siblingCount, currentPage]);
};

type PaginationProps = {
  onPageChange: (page: number) => void;
  totalPageCount: number;
  siblingCount?: number;
  currentPage: number;
  pageSize?: number;
  className?: string;
};

const Pagination: React.FC<PaginationProps> = ({
  onPageChange,
  totalPageCount,
  siblingCount = 1,
  currentPage,
  className,
  pageSize
}) => {
  // Get pagination range
  const paginationRange =
    usePagination({
      currentPage,
      totalPageCount,
      siblingCount,
    }) || [];

  // If there are less than 2 items in pagination range, don't render component
  if (currentPage === 0) {
    return null;
  }

  // Always show pagination, even for single page
  if (totalPageCount === 1) {
    return (
      <PaginationWrapper className={className}>
        <PaginationList>
          {/* Left Arrow - disabled for single page */}
          <PaginationArrow $disabled={true} onClick={() => {}}>
            <img src={Back} alt="Back" />
          </PaginationArrow>

          {/* Single page number */}
          <PaginationItem $selected={true} onClick={() => onPageChange(1)}>
            1
          </PaginationItem>

          {/* Right Arrow - disabled for single page */}
          <PaginationArrow $disabled={true} onClick={() => {}}>
            <img src={Next} alt="Next" />
          </PaginationArrow>
        </PaginationList>
      </PaginationWrapper>
    );
  }

  // If there are less than 2 items in pagination range for multi-page, don't render
  if (paginationRange.length < 2) {
    return null;
  }

  const onNext = () => {
    if (currentPage < totalPageCount) {
      onPageChange(currentPage + 1);
    }
  };

  const onPrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const lastPage = paginationRange[paginationRange.length - 1];

  return (
    <PaginationWrapper className={className}>
      <PaginationList>
        {/* Left Arrow */}
        <PaginationArrow $disabled={currentPage === 1} onClick={onPrevious}>
          <img src={Back} alt="Back" />
        </PaginationArrow>

        {/* Page Numbers */}
        {paginationRange.map((pageNumber, index) => {
          if (pageNumber === DOTS) {
            return (
              <PaginationItem key={`dots-${index}`} className="dots">
                &#8230;
              </PaginationItem>
            );
          }

          return (
            <PaginationItem
              key={`page-${pageNumber}`}
              $selected={pageNumber === currentPage}
              onClick={() => onPageChange(Number(pageNumber))}
            >
              {pageNumber}
            </PaginationItem>
          );
        })}

        {/* Right Arrow */}
        <PaginationArrow $disabled={currentPage === lastPage} onClick={onNext}>
          <img src={Next} alt="Next" />
        </PaginationArrow>
      </PaginationList>
    </PaginationWrapper>
  );
};

export default Pagination;
